import React, { useMemo } from "react";
import { generateFieldId } from "../../utils/common";
import { Checkbox, type CheckboxProps } from "./Checkbox";
import { Grid } from "../../layouts/Grid";
import { Stack } from "../../layouts/Stack";
import styles from "./Checkbox.module.css";

type LayoutProps = {
  columns?: number;
  direction?: "horizontal" | "vertical";
  "aria-label"?: string;
  children: React.ReactNode;
};

const CheckboxGroupLayout = ({
  columns,
  direction,
  "aria-label": ariaLabel,
  children
}: LayoutProps) => {
  if (columns) {
    return (
      <Grid
        role="group"
        aria-label={ariaLabel}
        columns={columns}
        rowGap="8"
        columnGap="8"
      >
        {children}
      </Grid>
    );
  }

  const isHorizontal = direction === "horizontal";
  return (
    <Stack
      role="group"
      aria-label={ariaLabel}
      direction={isHorizontal ? "horizontal" : "vertical"}
      gap="8"
      dangerouslySetClassName={
        isHorizontal ? styles.catalystCheckboxGroupWrap : undefined
      }
    >
      {children}
    </Stack>
  );
};

export type CheckboxGroupProps = {
  name: string;
  value?: string[];
  onChange?: (values: string[]) => void;
  disabled?: boolean;
  direction?: "horizontal" | "vertical";
  columns?: number;
  "aria-label"?: string;
  children:
    | React.ReactElement<CheckboxProps>
    | React.ReactElement<CheckboxProps>[];
};

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  value = [],
  onChange,
  disabled = false,
  direction = "vertical",
  columns,
  "aria-label": ariaLabel,
  children
}) => {
  const checkboxGroupName = useMemo(() => generateFieldId(name), [name]);

  const handleChange = (
    checked: boolean,
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!onChange) return;

    const newValues = checked
      ? [...value, evt.target.value]
      : value.filter(v => v !== evt.target.value);

    onChange(newValues);
  };

  return (
    <CheckboxGroupLayout
      columns={columns}
      direction={direction}
      aria-label={ariaLabel || name}
    >
      {React.Children.map(
        children,
        (checkbox: React.ReactElement<CheckboxProps>) => {
          if (checkbox?.type !== Checkbox) {
            throw new Error(
              "CheckboxGroup only accepts Checkbox components as children"
            );
          }

          return React.cloneElement(checkbox, {
            ...checkbox.props,
            name: checkboxGroupName,
            checked: checkbox.props.value
              ? value.includes(checkbox.props.value)
              : false,
            onChange: (
              checked: boolean,
              evt: React.ChangeEvent<HTMLInputElement>
            ) => {
              handleChange(checked, evt);
              checkbox.props.onChange?.(checked, evt);
            },
            disabled: disabled || checkbox.props.disabled
          });
        }
      )}
    </CheckboxGroupLayout>
  );
};
